import React from 'react';
import { ThemeProvider } from 'styled-components';
import { BrowserRouter } from 'react-router-dom';
import ErrorBoundary from '@containers/utility/ErrorBoundary';
import DynamicScripts from '@containers/utility/DynamicScripts';
import { PageTracking } from '@containers/utility/PageTracking';
import { useGlobalTheme } from '@hooks/application/useColorTheme';
import GlobalStyler from '@containers/utility/GlobalStyling';
import Router from './Router';
import { isSignedIn } from '@services/authentication';
import { AuthTokenManager } from '@classes/AuthTokenManager';
import { Provider, useDispatch } from 'react-redux';
import { getDataForCache, getRequiredData, handleAppTrackingInit } from '@utils/initialization';
import * as Sentry from '@sentry/react';
import { Managers } from '@containers/managers';
import { TrackingEvent } from '@classes/TrackingEvent';
// import { initAmplitudeInstance, logAmplitudeEvent } from '@utils/amplitude';
import store from '@utils/store';
import { AdvancedSettingsModal } from '@containers/DigitalAnalyst/AdvancedSettings';
import { ApplicationErrorMessageBanner } from '@containers/application/ApplicationErrorMessageBanner';
import { ApplicationSuccessMessageBanner } from '@containers/application/ApplicationSuccessMessageBanner';
import { Header } from '@app/navigation/header';

import 'core-js/stable';
import 'regenerator-runtime/runtime';

const Loading = () => {
  return <Header isLoadingState />;
};

function AppInitialization() {
  const dispatch = useDispatch();
  const [isReady, setIsReady] = React.useState(false);

  React.useEffect(() => {
    AuthTokenManager.init();
    let cancelled = false;
    const init = async () => {
      const isAuthed = isSignedIn();
      if (cancelled) return;
      const transaction = Sentry.startTransaction({ name: 'get-session-start-required-data' });
      if (isAuthed) {
        const responses = await getRequiredData(dispatch);
        const currentUserResponse = responses[1];
        await handleAppTrackingInit(currentUserResponse);
        setIsReady(true);
        await getDataForCache(dispatch);
      } else {
        setIsReady(true);
      }
      transaction?.finish();
    };

    init();

    return () => {
      cancelled = true;
    };
  }, []);

  if (!isReady) {
    return <Loading />;
  }

  return <Router />;
}

function AppBody() {
  return (
    <>
      <GlobalStyler />
      <BrowserRouter>
        <ErrorBoundary>
          <Managers />
          <DynamicScripts />
          <PageTracking />
          <AppInitialization />
          <AdvancedSettingsModal />
          <ApplicationErrorMessageBanner />
          <ApplicationSuccessMessageBanner />
        </ErrorBoundary>
      </BrowserRouter>
    </>
  );
}

function AppTheme() {
  const theme = useGlobalTheme();
  return (
    <ThemeProvider theme={theme}>
      <AppBody />
    </ThemeProvider>
  );
}

function Init() {
  const [isReady, setIsReady] = React.useState(false);

  React.useEffect(() => {
    // initAmplitudeInstance();
    TrackingEvent.addDefaultProperties({ 'Is Digital Analyst': true });
    // No-op event sender to prevent console errors
    TrackingEvent.addEventSender((name, properties) => {
      // Tracking events are currently disabled
    });

    // Suppress React prop warnings
    const originalError = console.error;
    console.error = (...args) => {
      const message = args[0];
      if (typeof message === 'string' && message.includes('React does not recognize')) {
        // Suppress unrecognized prop warnings
        return;
      }
      if (typeof message === 'string' && message.includes('non-boolean')) {
        // Suppress failed to parse source map warnings
        return;
      }
      originalError(...args);
    };

    setIsReady(true);
  }, []);

  if (!isReady) {
    return null;
  }

  return (
    <Provider store={store}>
      <AppTheme />
    </Provider>
  );
}

export default Init;
